import { Alert } from '@neo/primitives/alert'
import { ButtonWithLoader } from '@neo/primitives/button'
import { ReactComponent as PlivoLogo } from '@shared/assets/illustrations/login/plivo.svg'
import { useState } from 'react'
import { useNavigate } from 'router-v6'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useCompleteOnboardingMutation } from '../queries'
import { finishOnboarding } from './finish-onboarding'
import { StepBuild } from './step-build'
import { INITIAL_WIZARD_STATE, isComplete, type WizardState } from './wizard-data'

// A SINGLE-STEP wizard: one question, "what do you want to start with", with an
// inline framework probe under the two bring-your-own-stack answers.
//
// The legacy business-type question that used to come first was dropped. Its
// answer survives only as finishOnboarding's `businessType` argument, passed as
// '' — core treats business_type as optional and the payload builder omits an
// empty string, so nothing downstream had to change.
export const OnboardingWizard = () => {
  const navigate = useNavigate()
  const { data: profile, isLoading: isProfileLoading } = useProfileQuery()
  const { mutate: completeOnboarding, isPending } = useCompleteOnboardingMutation()
  const [state, setState] = useState<WizardState>(INITIAL_WIZARD_STATE)

  // Functional update — same-tick patches can't drop each other. Answers are
  // persisted once, on successful finish (finish-onboarding.ts): every reader
  // lives on a post-finish page, so half-typed abandoned answers never need
  // to reach localStorage.
  const update = (patch: Partial<WizardState>) => setState((prev) => ({ ...prev, ...patch }))

  const complete = isComplete(state)

  // Success navigates (replacing /onboarding in history); failure keeps the
  // user here with answers intact — see finish-onboarding.ts.
  const finish = () =>
    finishOnboarding({
      state,
      businessType: '',
      org: profile?.organizations?.[0],
      completeOnboarding,
      navigate,
    })

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Logo pinned: it sits OUTSIDE the scroller below, so long step-2 content
          scrolls under it rather than carrying it off-screen. */}
      <div className="w-full shrink-0 px-6 pt-6">
        <PlivoLogo aria-label="Plivo" className="h-6 w-auto" />
      </div>

      {/* The scroller is FULL-WIDTH with the content column centred inside it,
          not the centred column itself — a scroll container capped at max-w-2xl
          puts its scrollbar at the column's edge, mid-screen. This way it sits
          at the viewport's right edge. min-h-0 on both: flex children refuse to
          shrink below content otherwise, and the overflow never engages. */}
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        {/* Credits line, build options, then a right-aligned action row holding
            the single Get started button — there is nothing to go back to on a
            one-step wizard, so no secondary action sits beside it. */}
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 px-6 pb-8 pt-6">
          <Alert
            variant="success"
            content={
              <>
                Help us with a few quick questions, and we'll get your account ready with{' '}
                <span className="text-s-600">Free Credits</span>
              </>
            }
          />

          <StepBuild state={state} update={update} />
          <div className="flex items-center justify-end">
            <ButtonWithLoader
              onClick={finish}
              // Also gated on the profile: submitting before it resolves would
              // navigate away without ever posting, and the post-signup
              // redirect would bounce the user back into the wizard.
              disabled={!complete || isProfileLoading}
              loading={isPending}
              data-testid="onboarding-get-started-btn"
            >
              Get started
            </ButtonWithLoader>
          </div>
        </div>
      </div>
    </div>
  )
}
